import { useEffect, useState } from 'react';
import { getSiteSettings } from '@/lib/api';

/* Shared cache so the settings are only fetched once across the app. */
let _cache = null;
let _inflight = null;
const subscribers = new Set();

const PREVIEW_KEY = 'mt_preview_settings';
const isPreviewMode = () =>
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('preview') === '1';

const readPreview = () => {
    try {
        const raw = localStorage.getItem(PREVIEW_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
};

async function load() {
    if (isPreviewMode()) {
        return readPreview() || (_cache = await getSiteSettings());
    }
    if (_cache) return _cache;
    if (!_inflight) _inflight = getSiteSettings().then((d) => { _cache = d; _inflight = null; subscribers.forEach((s) => s(d)); return d; });
    return _inflight;
}

export function invalidateSettings() { _cache = null; }

/** Admin uses this to push the in-progress edit into the preview localStorage slot. */
export function pushPreviewSettings(data) {
    try {
        localStorage.setItem(PREVIEW_KEY, JSON.stringify(data));
    } catch { /* ignore quota / disabled */ }
}

export function clearPreviewSettings() {
    try { localStorage.removeItem(PREVIEW_KEY); } catch { /* localStorage unavailable */ }
}

export default function useSiteSettings() {
    const [settings, setSettings] = useState(isPreviewMode() ? readPreview() : _cache);

    useEffect(() => {
        let alive = true;
        load().then((d) => { if (alive) setSettings(d); });

        const sub = (d) => { if (alive) setSettings(d); };
        subscribers.add(sub);

        // In preview mode, listen for live updates from the admin tab.
        let onStorage;
        if (isPreviewMode()) {
            onStorage = (e) => {
                if (e.key === PREVIEW_KEY) {
                    const next = readPreview();
                    if (alive && next) setSettings(next);
                }
            };
            window.addEventListener('storage', onStorage);
        }
        return () => {
            alive = false;
            subscribers.delete(sub);
            if (onStorage) window.removeEventListener('storage', onStorage);
        };
    }, []);

    return settings;
}
